<template>
	<!-- eslint-disable-next-line vue/no-v-model-argument -->
	<van-popup v-model:show="show" position="bottom" closeable safe-area-inset-bottom @close="close">
		<div class="title">完善咨询信息</div>
		<div class="contentBox">
			<div class="productInfoBox">
				<div class="productImg">
					<img :src="productImage" alt="" @error="handleProductImageError" />
				</div>
				<div class="productInfo">
					<div class="productTitle ellipsis">{{ product?.productName || '--' }}</div>
					<div class="productContent ellipsis">规格型号：{{ product?.specifications || '--' }}</div>
					<div class="productContent ellipsis">所在公司：{{ product?.filialeName || product?.companyName || '--' }}</div>
				</div>
			</div>
			<div>
				<van-field class="mb-2" disabled v-model="form.customerCompanyName" label="公司名称" clearable :label-width="80" />
				<van-field
					class="mb-2"
					v-model="form.contacts"
					label="联系人 "
					maxlength="10"
					placeholder="请输入联系人"
					required
					clearable
					:label-width="80"
				/>
				<van-field class="mb-2" v-model="form.phoneNumber" label="手机号" placeholder="请输入手机号" required clearable :label-width="80" />
				<van-field readonly is-link :label-width="80" v-model="form.area" label="区域" placeholder="请选择区域" @click="areaClick" />
				<van-field
					v-model="form.contents"
					rows="3"
					type="textarea"
					label="咨询内容"
					maxlength="500"
					autosize
					placeholder="请输入咨询内容"
					clearable
					:label-width="80"
					class="mb-2"
				/>
			</div>
		</div>

		<div v-show="!areaShow" class="btnBox">
			<van-button class="btn" @click="close">取消</van-button>
			<van-button class="btn" type="info" :loading="loading" @click="submit">我要咨询</van-button>
		</div>
		<!-- eslint-disable-next-line vue/no-v-model-argument -->
		<van-popup v-model:show="areaShow" position="bottom" :style="{ height: '308px' }" @close="closeArea">
			<van-area
				ref="areaRef"
				v-model="form.areaCode"
				:area-list="areaList"
				@cancel="closeArea"
				@confirm="confirmArea"
				:columns-num="2"
				title="请选择省市"
			/>
		</van-popup>
	</van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { showToast } from 'vant';
import AreaList from './area';
import { fetchKfConfig } from '@/utils/kf.js';
import { addPotentialCustomer } from '@/api/user';
import { getStorageSync, setStorageSync } from '@/utils/storage.js';
import { useIsGxzzSite } from '@/composables/useIsGxzzSite';
import { useAnalytics } from '@/composables/useAnalytics';

const props = defineProps({
	showPopup: {
		type: Boolean,
		default: false,
	},
	product: {
		type: Object,
		default: () => ({}),
	},
	trackingChannel: {
		type: String,
		default: '',
	},
});

const emit = defineEmits(['close']);

const route = useRoute();
const { trackEvent } = useAnalytics();

/** 当前访问站点 companyId：路由优先，否则取商品上的站点 id（与国械智造判断同源） */
const siteCompanyIdForKf = computed(() => {
	const routeCompanyId = route.params?.companyId;
	if (routeCompanyId != null && routeCompanyId !== '') {
		const single = Array.isArray(routeCompanyId) ? routeCompanyId[0] : routeCompanyId;
		return String(single);
	}
	const productCompanyId = props.product?.companyId ?? props.product?.companyid;
	if (productCompanyId != null && productCompanyId !== '') {
		return String(productCompanyId);
	}
	return '';
});

const isGxzzSite = useIsGxzzSite(siteCompanyIdForKf);

const loading = ref(false);
const show = ref(false);
const areaShow = ref(false);
const isSdkReady = ref(false);
/** 当前咨询是否已提交成功，避免关闭弹窗时误报取消。 */
const consultationSubmitted = ref(false);
/** 当前弹窗取消点击是否已上报，避免按钮与关闭事件重复上报。 */
const cancelClickTracked = ref(false);
const areaRef = ref(null);
const areaList = AreaList;
const userInfo = ref({});
const form = ref({
	customerCompanyName: '',
	contacts: '',
	phoneNumber: '',
	area: '',
	areaCode: '',
	areaList: '',
	contents: '',
	province: '',
	city: '',
});

const PRODUCT_IMG_PLACEHOLDER = '/staticSeo/images/placeholder.png';

function pickProductImageSrc(product) {
	const candidates = [product?.defaultImageUrl, product?.defaultimageurl, product?.picUrl, product?.mainPicUrl, product?.imageUrl];
	const found = candidates.find(value => typeof value === 'string' && value.trim());
	return found?.trim() || PRODUCT_IMG_PLACEHOLDER;
}

/** 七鱼 product.picture 需可访问的完整 URL */
function toAbsoluteProductImageUrl(url) {
	const src = (url || '').trim() || PRODUCT_IMG_PLACEHOLDER;
	if (/^https?:\/\//i.test(src)) return src;
	if (import.meta.client) {
		return `${window.location.origin}${src.startsWith('/') ? src : `/${src}`}`;
	}
	return src;
}

const productImage = computed(() => pickProductImageSrc(props.product));

function handleProductImageError(e) {
	e.target.src = PRODUCT_IMG_PLACEHOLDER;
}

watch(
	() => props.showPopup,
	newVal => {
		show.value = newVal;
		if (newVal) {
			if (props.trackingChannel) {
				trackEvent({
					module: '咨询弹窗',
					eventCode: 'consult_popup_exposure',
					eventName: '咨询弹窗曝光',
					attrName: 'channel_type',
					attrCode: props.trackingChannel,
				});
			}
			init();
		}
	}
);

function resetForm() {
	form.value.contacts = '';
	form.value.phoneNumber = '';
	form.value.area = '';
	form.value.areaCode = '';
	form.value.areaList = '';
	form.value.contents = '';
	form.value.province = '';
	form.value.city = '';
}

/** 与 PC `utils/kf.js` 一致：有 ysf 则只挂 onready；否则插入脚本，onload 后再挂。uuid 在就绪时写入。 */
const QIYU_SCRIPT_SRC = 'https://qiyukf.com/script/1a8286b648bf73c059ceda900e23291f.js?hidden=1';

function setupYsfOnReady() {
	const markReady = () => {
		if (isSdkReady.value) return;
		isSdkReady.value = true;
		if (!getStorageSync('uuid')) {
			setStorageSync('uuid', Math.random().toString(36).slice(-10));
		}
	};
	try {
		window.ysf?.('onready', markReady);
	} catch {
		markReady();
	}
	// 七鱼 SDK 已在别处初始化完毕时，onready 可能不会再触发，短延迟兜底
	setTimeout(markReady, 600);
}

function injectQiyuStubAndScript() {
	(function (w, d, n, a, j) {
		w[n] =
			w[n] ||
			function () {
				return (w[n].a = w[n].a || []).push(arguments);
			};
		j = d.createElement('script');
		j.async = true;
		j.src = QIYU_SCRIPT_SRC;
		j.onload = () => setupYsfOnReady();
		d.body.appendChild(j);
	})(window, document, 'ysf');
}

function initQiyuSdk() {
	if (!import.meta.client) return;
	if (window.ysf) {
		setupYsfOnReady();
		return;
	}
	injectQiyuStubAndScript();
}

/** 弹窗打开后用户可能立即提交，等待七鱼 onready */
function waitForIsSdkReady(timeoutMs = 8000) {
	return new Promise(resolve => {
		if (isSdkReady.value) {
			resolve(true);
			return;
		}
		const start = Date.now();
		const timer = setInterval(() => {
			if (isSdkReady.value || Date.now() - start >= timeoutMs) {
				clearInterval(timer);
				resolve(isSdkReady.value);
			}
		}, 50);
	});
}

function init() {
	initQiyuSdk();
	userInfo.value = getStorageSync('user') || {};
	consultationSubmitted.value = false;
	cancelClickTracked.value = false;
	resetForm();
	form.value.customerCompanyName = userInfo.value.customerCompanyName || '';
}

/**
 * 上报咨询弹窗按钮点击。
 * @param {string} buttonName 按钮名称
 */
function trackConsultPopupClick(buttonName) {
	if (!buttonName) return;
	trackEvent({
		companyCode: props.product?.companycode || props.product?.filialeCode || '',
		companyName: props.product?.companyName || props.product?.filialeName || '',
		module: '咨询弹窗',
		eventCode: 'onsult_popup_click',
		eventName: '咨询弹窗点击',
		attrName: 'button_name',
		attrCode: buttonName,
	});
}

function close() {
	if (!consultationSubmitted.value && !cancelClickTracked.value) {
		cancelClickTracked.value = true;
		trackConsultPopupClick('取消');
	}
	show.value = false;
	emit('close');
}

function areaClick() {
	areaShow.value = true;
}

function confirmArea(payload) {
	// 兼容两种返回:
	// 1) Vant4: confirm(selectedOptions[])
	// 2) 其他实现: confirm({ selectedOptions, selectedValues })
	const selectedOptionsRaw = Array.isArray(payload) ? payload : payload?.selectedOptions;
	const selectedValuesRaw = Array.isArray(payload?.selectedValues) ? payload.selectedValues : null;
	const selectedOptions = Array.isArray(selectedOptionsRaw) ? selectedOptionsRaw : [];

	const selectedValues = (selectedValuesRaw || selectedOptions.map(item => item?.value || item?.code || '')).filter(Boolean);
	const selectedTexts = selectedOptions.map(item => item?.text || item?.name || '').filter(Boolean);

	form.value.areaCode = selectedValues[selectedValues.length - 1] || '';
	form.value.areaList = selectedValues.join(',');
	form.value.area = selectedTexts.join('/');
	form.value.province = selectedTexts[0] || '';
	form.value.city = selectedTexts[1] || '';
	closeArea();
}

function closeArea() {
	areaShow.value = false;
}

function buildCustomerServiceData() {
	return [
		{ index: 0, key: 'userName', label: '客户账号', value: userInfo.value?.userName || '' },
		{ index: 1, key: 'customerCompanyName', label: '公司名称', value: form.value.customerCompanyName || '' },
		{ index: 2, key: 'contacts', label: '意向联系人', value: form.value.contacts || '' },
		{ index: 3, key: 'phoneNumber', label: '意向手机号', value: form.value.phoneNumber || '' },
		{ index: 4, key: 'area', label: '区域', value: form.value.area || '' },
		{ index: 5, key: 'contents', label: '咨询内容', value: form.value.contents || '' },
		{ index: 6, key: 'productCompanyName', label: '咨询商品所属站点', value: props.product?.companyName || props.product?.filialeName || '' },
	];
}

function reportProductCard() {
	if (!import.meta.client || !window.ysf) return;
	const productData = {
		productCode: props.product?.productCode || props.product?.productcode || '',
		specifications: props.product?.specifications || props.product?.specs || '',
		productName: props.product?.productName || props.product?.productname || '',
	};
	const desc = `编码：${productData.productCode} / 规格：${productData.specifications}`;
	window.ysf('product', {
		show: 1,
		title: productData.productName,
		desc,
		picture: toAbsoluteProductImageUrl(productImage.value),
		note: props.product?.companyName || '',
		url: window.location.href,
	});
}

async function openSdk(uid) {
	try {
		if (!import.meta.client || !window.ysf) return;
		await waitForIsSdkReady();
		if (!isSdkReady.value) {
			showToast({ message: '客服暂不可用，请稍后重试', position: 'bottom' });
			return;
		}
		const isLogin = !!getStorageSync('cmdc_access_token');
		// 仅国械智造站点使用当前访问站点 id；其它站点一律走总站 1
		const companyIdForKf = isGxzzSite.value && siteCompanyIdForKf.value ? Number(siteCompanyIdForKf.value) || siteCompanyIdForKf.value : 1;
		const requestData = {
			companyId: companyIdForKf,
			userId: isLogin ? userInfo.value?.userId : undefined,
			isGxzz: isGxzzSite.value,
		};
		const res = await fetchKfConfig(requestData);
		if (!res?.success || !res?.data) return;
		const configData = { ...res.data, uid, data: JSON.stringify(buildCustomerServiceData()) };
		window.ysf('config', configData);
		reportProductCard();
		window.ysf('open');
	} catch (error) {
		// 咨询提交成功后，客服 SDK 初始化失败不应影响主流程
	}
}

async function submit() {
	if (loading.value) return;
	const contacts = (form.value.contacts || '').trim();
	const phoneNumber = (form.value.phoneNumber || '').trim();
	const contents = (form.value.contents || '').trim();
	if (!contacts) {
		showToast('请输入联系人');
		return;
	}
	if (!phoneNumber) {
		showToast('请输入联系人手机号');
		return;
	}
	if (!/^1\d{10}$/.test(phoneNumber)) {
		showToast('请输入正确手机号');
		return;
	}
	trackConsultPopupClick('提交信息');
	loading.value = true;
	try {
		const params = {
			consultationType: 1,
			productId: props.product?.productId,
			productLink: import.meta.client ? window.location.href : '',
			...form.value,
			contacts,
			phoneNumber,
			contents,
			sourceType: 2,
			productName: props.product?.productName || props.product?.productname || '',
			productCode: props.product?.productCode || props.product?.productcode || '',
			companyName: props.product?.companyName || props.product?.filialeName || '',
			companyCode: props.product?.companycode || props.product?.filialeCode || '',
		};
		const { code, data, message } = await addPotentialCustomer(params);
		if (code == 200) {
			showToast({ message: '提交成功', position: 'bottom' });
			await openSdk(data);
			consultationSubmitted.value = true;
			close();
			resetForm();
			return;
		}
		showToast(message || '提交失败');
	} catch (error) {
		showToast('提交失败，请稍后重试');
	} finally {
		loading.value = false;
	}
}
</script>

<style scoped lang="scss">
.title {
	height: 44px;
	border-radius: 5px 5px 0 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	text-align: center;
	border-bottom: 1px solid #eee;
}

.contentBox {
	padding: 10px 0 0;
}

.mb-2 {
	margin-bottom: 2px;
}

.productInfoBox {
	padding: 0 19px 10px 12px;
	width: 100%;
	height: 80px;
	display: flex;
	gap: 12px;

	.productImg {
		border-radius: 5px;
		overflow: hidden;

		img {
			width: 80px;
			height: 80px;
			object-fit: cover;
		}
	}

	.productInfo {
		width: 252px;
		font-size: 12px;
		font-weight: 400;
		line-height: 18px;
		color: #595959;

		.productTitle {
			font-size: 15px;
			font-weight: 500;
			line-height: 21px;
			margin-bottom: 8px;
		}

		.productContent {
			display: block;
		}
	}
}

.btnBox {
	height: 54px;
	padding: 0 16px;
	margin-top: 10px;
	display: flex;
	gap: 12px;
	align-items: center;
	justify-content: space-between;

	.btn {
		width: 169.5px;
		height: 40px;
	}
}

:deep(.van-field::after) {
	border-bottom: 2px solid #ebebeb;
}

:deep(.van-field__label) {
	color: #595959;
}

:deep(.van-field__control) {
	font-size: 14px;
	color: #1f1f1f;
}

:deep(.van-field__control:disabled) {
	-webkit-text-fill-color: #1f1f1f;
}

:deep(.van-field__body textarea) {
	background-color: #f7f8f9;
}
</style>
